import Check from '@/components/Check';
import Link from '@/components/Link';
import MarketingSection from './MarketingSection';

function MarketingPricingPublicPrice({ publicPrice }) {
  return (
    <div className="flex items-center pb-1">
      <div className="font-medium">Total de programas</div>
      <div className="block flex-1 mx-4 border-t-2 border-dashed border-gray-200" />
      <div className="line-through text-red-300 font-medium">
        {`$${publicPrice}`}
      </div>
    </div>
  );
}

function MarketingPricingPromotionalDiscount({ promotionalDiscount }) {
  return (
    <div className="flex items-center pb-1">
      <div className="font-medium">Descuento promocional</div>
      <div className="block flex-1 mx-4 border-t-2 border-dashed border-gray-200" />
      <div className="text-red-300 font-medium">
        {`-$${promotionalDiscount}`}
      </div>
    </div>
  );
}

function MarketingPricingEarlyBirdDiscount({ priceEarlyBirdDiscount }) {
  return (
    <div className="items-center hidden">
      <span className="font-medium">Descuento Early Bird</span>
      <span className="block flex-1 mx-4 border-t border-dashed border-gray-300"></span>
      <span className="text-red-300 font-medium">
        {`-$${priceEarlyBirdDiscount}`}
      </span>
    </div>
  );
}

function MarketingPricingByProgram({
  priceByProgram,
  publicPrice,
  promotionalDiscount,
  priceEarlyBirdDiscount,
}) {
  const hasPublicPrice = publicPrice !== null;
  const hasPromotionalDiscount = promotionalDiscount !== null;
  const hasPriceEarlyBirdDiscount = priceEarlyBirdDiscount !== null;

  return (
    <div className="bg-white shadow rounded-xl mt-16 mx-auto md:max-w-xs lg:mx-0 lg:max-w-full lg:rounded-r-none lg:min-w-0 lg:w-2/5 lg:mt-0 overflow-hidden">
      <div className="px-8 py-6 bg-gray-100">
        <h3 className="text-gray-700 text-lg sm:text-xl text-center font-bold my-0">
          Programas
        </h3>
      </div>
      <div className="py-10 px-10 space-y-6 divide-y divide-gray-200">
        <ul className="text-gray-400 text-sm">
          {priceByProgram.map(({ subject, referencialPrice }, index) => (
            <li
              key={`pricing-program-${index}`}
              className="flex items-center mb-2"
            >
              <div className="font-medium">{subject}</div>
              <div className="block flex-1 mx-4 border-t-2 border-dashed border-gray-200" />
              <div className="line-through font-medium text-gray-300">
                {`$${referencialPrice}`}
              </div>
            </li>
          ))}
        </ul>
        <div className="pt-6 text-sm">
          {hasPublicPrice && (
            <MarketingPricingPublicPrice publicPrice={publicPrice} />
          )}
          {hasPromotionalDiscount && (
            <MarketingPricingPromotionalDiscount
              promotionalDiscount={promotionalDiscount}
            />
          )}
          {hasPriceEarlyBirdDiscount && (
            <MarketingPricingEarlyBirdDiscount
              priceEarlyBirdDiscount={priceEarlyBirdDiscount}
            />
          )}
        </div>
      </div>
      <div className="pb-10 px-10 md:hidden">
        <Link
          className="w-full text-center text-lg sm:text-xl inline-block rounded-full focus:outline-none bg-green-500 shadow hover:bg-green-600 focus:bg-green-700 text-white font-semibold px-6 py-2 sm:py-3"
          href="/clases"
        >
          Inscríbete hoy mismo
        </Link>
      </div>
    </div>
  );
}

function MarketingPricingTableContent({ pricingTableContent }) {
  return (
    <ul className="px-10 space-y-2">
      {pricingTableContent.map(({ text }, index) => (
        <li
          key={`pricing-table-content-${index}`}
          className="flex items-start space-x-3"
        >
          <div className="text-green-400 mt-px">
            <Check />
          </div>
          <div className="font-normal text-gray-600">{text}</div>
        </li>
      ))}
    </ul>
  );
}

export default function MarketingPricing({
  price,
  pricingTableContent,
  priceWithDiscount,
  promotionalDiscount,
  publicPrice,
  priceByProgram,
}) {
  const hasPricingTableContent = pricingTableContent !== null;
  const hasPricingByProgram = priceByProgram !== null;

  return (
    <MarketingSection
      className="bg-gray-900 text-white"
      sectionTitle="Obtén tu cupo hoy mismo accediento a los precios de oferta"
    >
      <div className="block lg:flex lg:items-center lg:justify-center lg:flex-row-reverse text-gray-900">
        <div className="mx-auto w-full md:max-w-lg lg:mx-0 lg:min-w-0 lg:w-3/5">
          <div className="bg-white shadow-xl rounded-lg lg:rounded-xl w-full relative overflow-hidden">
            <div className="px-8 py-6 bg-gray-100">
              <h3 className="text-gray-900 text-lg sm:text-xl text-center my-0 space-x-2 flex items-center justify-center">
                <div className="font-bold">Precio referencial desde</div>
                <div className="text-gray-400 text-lg">*</div>
              </h3>
            </div>
            <div className="px-10 lg:py-4">
              <div className="text-center py-6 lg:py-4">
                <span className="hidden -ml-8 text-right text-2xl leading-8 font-semibold text-gray-400 tracking-wide line-through sm:text-3xl sm:leading-9">
                  ${price}
                </span>
                <span className="inline-flex items-center font-display text-5xl md:text-6xl font-bold text-black mr-3 sm:mr-4">
                  <span className="text-2xl md:text-3xl">$</span>
                  <span>{price}</span>
                </span>
                <span className="inline-flex items-center font-display text-xl md:text-2xl font-medium text-gray-400 mr-3 sm:mr-4">
                  USD
                </span>
              </div>
            </div>
            {hasPricingTableContent && (
              <MarketingPricingTableContent
                pricingTableContent={pricingTableContent}
              />
            )}
            <div className="text-center mt-12 px-10 py-6 space-y-4 bg-gray-100 rounded-b-lg lg:rounded-b-xl">
              <Link
                className="w-full text-lg sm:text-xl inline-block rounded-full focus:outline-none bg-red-500 shadow hover:bg-red-600 focus:bg-red-700 text-white font-semibold px-6 py-2 sm:py-3"
                href="/clases"
              >
                Obtén esta promoción hoy mismo
              </Link>
            </div>
          </div>
        </div>
        {hasPricingByProgram && (
          <MarketingPricingByProgram
            publicPrice={publicPrice}
            priceByProgram={priceByProgram}
            promotionalDiscount={promotionalDiscount}
          />
        )}
      </div>
    </MarketingSection>
  );
}
